import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Alert, Button, Card } from 'react-bootstrap';
import moment from 'moment';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner';
import UserReviewDateResource from '../../common/resource/UserReviewDateResource';
import { Icon, LoadingAnimation } from '../../common/components/Utilities';
import UserReviewResource from '../../common/resource/UserReviewResource';
import UserIcon from '../../common/components/UserIcon';
import ReviewCategoriesResource from '../../common/resource/ReviewCategoriesResource';
import MarkdownRenderer from '../../common/components/MarkdownRenderer';

function Reviews({ user, activeUser }) {
  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [selectedDatePublished, setSelectedDatePublished] = useState(null);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [newReviewModalOpen, setNewReviewModalOpen] = useState(false);

  const loadUserDates = async () => {
    setLoading(true);
    const data = await UserReviewDateResource.index(user.id);
    setDates(data);

    const selectedDateData = data.find((dateData) => dateData.selected);
    setCurrentPeriod(selectedDateData === undefined ? null : selectedDateData.date);

    if (!selectedDate) {
      data.forEach((dateData) => {
        if (dateData.selected === true) {
          setSelectedDate(dateData.date);
        }
      });
    }
    setLoading(false);
  };

  const selectDate = (date, published) => {
    setSelectedDate(date);
    setSelectedReviewer(null);
    setGroupByCategory(true);
    setSelectedDatePublished(published);
  };

  const selectDateAndReviewer = (date, reviewer, published) => {
    setSelectedDate(date);
    setSelectedReviewer(reviewer);
    setGroupByCategory(false);
    setSelectedDatePublished(published);
  };

  const createNewReview = () => {
    setNewReviewModalOpen(true);
  };

  useEffect(() => { loadUserDates(); }, [user.id]);

  const getDateVariant = (date, active, current, userContext, published) => {
    if (date === active) {
      if (userContext) {
        return 'outline-success';
      }
      return 'success';
    }

    if (date === current) {
      if (userContext || date !== active) {
        return 'outline-primary';
      }
      return 'primary';
    }

    if (!published) {
      return 'outline-warning';
    }

    return 'outline-secondary';
  };

  const isSelected = (dateData, userData) => selectedDate === dateData.date
      && selectedReviewer === userData.id;

  return (
    <div className="content">
      <div className="container-fluid">
        <LoadingAnimation loading={loading} />
        <Row>
          <Col md={6}>
            {selectedDate && (
              <h2>
                {user.name}
                {' '}
                reviews for
                {' '}
                {moment(selectedDate).format('MMMM YYYY')}
              </h2>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={12} className="d-flex flex-row justify-content-between justify-content-end">
            {dates.length > 0 && dates.map((dateData) => (
              <div className="py-4" key={dateData.date}>
                <Button
                  variant={getDateVariant(
                    dateData.date,
                    selectedDate,
                    currentPeriod,
                    (selectedReviewer !== null),
                    dateData.published,
                  )}
                  size="sm"
                  onClick={() => selectDate(dateData.date, dateData.published)}
                  style={{
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'block',
                    margin: 'auto',
                  }}
                />
                <p className="success">{moment(dateData.date).format('MMMM YYYY')}</p>
                {dateData.reviewers && dateData.reviewers.map((userData) => (
                  <Button
                    key={`${dateData.date}-user-œ${userData.id}`}
                    style={{
                      display: 'block', textAlign: 'center', padding: 0, width: '100%',
                    }}
                    variant="outline-light"
                    disabled={!userData.visible}
                    onClick={
                      () => selectDateAndReviewer(dateData.date, userData.id, dateData.published)
                    }
                  >
                    <UserIcon
                      style={{
                        borderColor: isSelected(dateData, userData) ? '#28a745' : 'none',
                        borderWidth: isSelected(dateData, userData) ? 3 : 0,
                        borderStyle: isSelected(dateData, userData) ? 'solid' : 'none',
                        opacity: userData.visible ? 1 : 0.3,
                      }}
                      user={userData}
                      size={isSelected(dateData, userData) ? 26 : 23}
                      round
                    />
                  </Button>
                ))}
                {!dateData.current_user_review_exists
                    && !dateData.published
                    && dateData.date === currentPeriod
                    && (
                    <Button
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        padding: 0,
                        borderRadius: '50%',
                        height: 26,
                        width: 26,
                        margin: '0 auto',
                      }}
                      onClick={() => createNewReview()}
                    >
                      <Icon icon="plus" />
                    </Button>
                    )}
              </div>
            ))}
          </Col>
        </Row>
        <Row md={12}>
          {selectedDate && (
            <RenderReviews
              date={selectedDate}
              user={user}
              reviewer={selectedReviewer}
              groupByCategory={groupByCategory}
              allowAdding
              activeUser={activeUser}
              addAction={createNewReview}
              refreshAction={() => loadUserDates().then(
                () => selectDate(selectedDate, selectedDatePublished),
              )}
            />
          )}
        </Row>
        <NewReviewInputModal
          open={newReviewModalOpen}
          setOpen={setNewReviewModalOpen}
          user={user}
          date={selectedDate}
          refreshView={loadUserDates}
        />
      </div>
    </div>
  );
}

const getTitle = (category) => {
  switch (category) {
    case 'communication':
      return 'Communication';
    case 'motivation':
      return 'Motivation';
    case 'team_working':
      return 'Team Working';
    case 'role':
      return 'Role & Responsibility';
    case 'impact':
      return '💥 Impact';
    case 'knowledge':
      return '🎓 Knowledge';
    case 'behaviours':
      return '✨ Behaviours';
    default:
      return 'Other Comments';
  }
};

const getSimpleTitle = (category) => {
  switch (category) {
    case 'team_working':
      return 'team Working';
    case 'role':
      return 'role & Responsibility';
    default:
      return category;
  }
};

const getSubtitle = (category) => {
  switch (category) {
    case 'impact':
      return 'The size scope and value of what you do.';
    case 'knowledge':
      return 'Your knowledge of Gear4music, our systems & processes and specialist knowledge related to your role.';
    case 'behaviours':
      return 'How you interact with others, how you change the world around you for the better and how you help others to become better and more impactful.';
    default:
      return '';
  }
};

function RenderReviews({
  date, user, reviewer = false, groupByCategory, allowAdding, activeUser, addAction, refreshAction,
}) {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [selfReview, setSelfReview] = useState(null);
  const [leadReview, setLeadReview] = useState(null);
  const [otherReviews, setOtherReviews] = useState([]);

  const loadReview = async () => {
    setLoading(true);
    try {
      const params = {
        review_period: moment(date).format('YYYY-MM-DD'),
      };
      if (reviewer) {
        params.reviewer_id = reviewer;
      }

      const data = await UserReviewResource(user.id).index(params);
      setReviews(data);

      if (groupByCategory && data.length > 1) {
        setSelfReview(null);
        setLeadReview(null);
        setOtherReviews([]);
      } else {
        const selfReviews = data.filter((review) => (review.review_type === 'self'));
        setSelfReview(selfReviews.length > 0 ? selfReviews[0] : null);

        const leadReviews = data.filter((review) => (review.review_type === 'team'));
        setLeadReview(leadReviews.length > 0 ? leadReviews[0] : null);

        setOtherReviews(data.filter((review) => (review.review_type !== 'self' && review.review_type !== 'team')));
      }
    } catch (error) {
      setReviews([]);
      alert(`Something went wrong: ${error}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReview(); }, [date, user.id, reviewer]);

  const mapByCategory = (reviewData) => {
    const byCategory = [];
    reviewData.forEach((review) => {
      review.data_by_category.forEach((categoryData) => {
        if (!byCategory.find(
          (byCategoryData) => byCategoryData.category === categoryData.category,
        )) {
          byCategory.push({
            category: categoryData.category,
            categoryEntries: [],
          });
        }
        byCategory.find(
          (byCategoryData) => byCategoryData.category === categoryData.category,
        ).categoryEntries.push(categoryData);
      });
    });
    return byCategory;
  };

  const reloadView = () => {
    refreshAction().then(() => loadReview());
  };

  return (
    <div style={{ width: '100%', minHeight: loading && 100 }}>
      <LoadingAnimation loading={loading} />
      {reviews.length === 0 && (
      <div>
        <Alert variant="danger" className="mb-0">
          There are no reviews for this date
          {allowAdding && ' yet'}
          {', or you don\'t have permission to view them.'}
        </Alert>
      </div>
      )}
      {!reviews.find((review) => review.reviewer_id === activeUser.id) && (
      <div className="py-2">
        <Button onClick={addAction}>
          {user.id === activeUser.id ? 'Review yourself' : `Add a review for ${user.name}`}
        </Button>
      </div>
      )}
      {selfReview && (
        <RenderReview title="Self Review" review={selfReview} refreshView={reloadView} />
      )}
      {leadReview && (
        <RenderReview title={`Line Manager Review from ${leadReview.reviewer.name}`} review={leadReview} refreshView={reloadView} />
      )}
      {otherReviews.length > 0 && (
        <div>
          <h4>Team and Peer Reviews</h4>
          <div>
            {otherReviews.map((review) => (
              <RenderReview
                key={review.id}
                title={review.reviewer.name}
                review={review}
                refreshView={reloadView}
              />
            ))}
          </div>
        </div>
      )}
      {groupByCategory && reviews.length > 1 && mapByCategory(reviews).map((categoryData) => (
        <div key={categoryData.category} style={{ paddingBottom: 10 }}>
          <h3>{getTitle(categoryData.category)}</h3>
          <div className="d-flex flex-row justify-content-between flex-wrap" style={{ margin: '0px -10px' }}>
            <RenderReviewCategoryGroup reviewCategories={categoryData.categoryEntries} />
          </div>
        </div>
      ))}
    </div>
  );
}

function RenderReview({
  review, title, refreshView,
}) {
  const [editing, setEditing] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const publish = async () => {
    const params = {
      is_published: true,
    };

    setPublishing(true);
    try {
      await UserReviewResource(review.user_id).put(review.id, params);
    } catch (error) {
      alert(`Something went wrong: ${error}`);
      console.error(error);
    } finally {
      setPublishing(false);
      refreshView();
    }
  };

  return (
    <div style={{ paddingBottom: 10 }}>
      <h3>{title}</h3>
      <div className="d-flex flex-row justify-content-between flex-wrap" style={{ margin: '0px -10px' }}>
        <RenderReviewCategoryGroup reviewCategories={review.data_by_category} />
      </div>
      {!review.lead_has_published && (
        <Alert variant="danger">
          <div style={{
            lineHeight: (review.user_can_publish || review.editable_by_user) && '38px',
          }}
          >
            <span>
              This review has not been published yet.
              {' '}
              {review.review_type !== 'team' && (
              <span>

                {review.user.manager.name}
                {' '}
                will publish all reviews when ready.
              </span>
              )}
            </span>
            {publishing && <Spinner className="ml-2" style={{ height: 25, width: 25, margin: 'auto' }} animation="border" />}
            {review.user_can_publish && <Button onClick={publish} disabled={publishing} className="float-right ml-2">Publish</Button>}
            {review.editable_by_user
                        && <Button onClick={() => setEditing(true)} className="float-right">Edit</Button>}
          </div>
        </Alert>
      )}
      {review.lead_has_published && !review.all_notes_completed && (
        <Alert variant="danger">
          <div style={{
            lineHeight: (review.editable_by_user) && '38px',
          }}
          >
            <span>
              {'This review has been published but will not be visible until it\'s completed.'}
            </span>
            {review.editable_by_user
                    && <Button onClick={() => setEditing(true)} className="float-right">Edit</Button>}
          </div>
        </Alert>
      )}
      <EditReviewInputModal
        review={review}
        open={editing}
        setOpen={setEditing}
        user={review.user}
        date={review.review_period}
        refreshView={refreshView}
      />
    </div>
  );
}

function RenderReviewCategoryGroup({ reviewCategories }) {
  return (
    <>
      {reviewCategories.map((categoryData) => (
        <RenderCategoryCard
          key={`${categoryData.reviewer.id}-${categoryData.category}`}
          score={categoryData.score}
          category={categoryData.category}
          comment={categoryData.comment}
          reviewer={categoryData.reviewer}
          type={categoryData.type}
          commentPos={categoryData.comment_pos}
          commentNeg={categoryData.comment_neg}
        />
      ))}
    </>
  );
}

const getColour = (category) => {
  switch (category) {
    case 'communication':
    case 'behaviours':
      return 'info';
    case 'motivation':
    case 'knowledge':
      return 'success';
    case 'team_working':
    case 'impact':
      return 'secondary';
    case 'role':
      return 'primary';
    default:
      return 'light';
  }
};

const getText = (category) => {
  switch (category) {
    case 'other':
      return null;
    default:
      return 'white';
  }
};

const getBorder = (category) => {
  switch (category) {
    case 'other':
      return 'secondary';
    default:
      return null;
  }
};

const getRelation = (type) => {
  switch (type) {
    case 'team':
      return 'Line Manager';
    case 'self':
      return 'Self Review';
    case 'lead':
      return 'Team Member';
    default:
      return 'Peer';
  }
};

function CompletedPill({ reviewCategoryData, style }) {
  const totals = reviewCategoryData.map((categoryData) => {
    const toReturn = [];
    if (categoryData.comment !== undefined) {
      toReturn.total = 1;
      toReturn.completed = (categoryData.comment && categoryData.comment.length > 0);
    } else {
      // split
      toReturn.total = 2;
      toReturn.completed = (categoryData.comment_pos && categoryData.comment_pos.length > 0)
          + (categoryData.comment_neg && categoryData.comment_neg.length > 0);
    }
    return toReturn;
  }).reduce((carry, totalData) => {
    const toReturn = { ...carry };
    toReturn.total += totalData.total;
    toReturn.completed += totalData.completed;
    return toReturn;
  }, { total: 0, completed: 0 });

  const finished = totals.total > 0 ? Math.round((100 / totals.total) * totals.completed) : 0;

  return <ProgressBar style={style} variant="info" now={finished} label={`${finished}%`} />;
}

function RenderCategoryCard({
  category, score, comment = null, commentPos = null, commentNeg = null, reviewer, type,
}) {
  return (
    <Card
      style={{
        flex: 1,
        margin: '10px',
        minWidth: '30%',
      }}
      bg={getColour(category)}
      text={getText(category)}
      border={getBorder(category)}
    >
      <Card.Header>
        {getTitle(category)}
        {' '}
        {score && (
        <span style={{
          borderRadius: '50%',
          width: 32,
          height: 32,
          position: 'absolute',
          right: '10px',
          textAlign: 'center',
          background: '#fff',
          border: '2px solid #666',
          color: '#666',
        }}
        >
          {score}
        </span>
        )}
      </Card.Header>
      {comment && (
      <Card.Body>
        <MarkdownRenderer content={comment} />
      </Card.Body>
      )}
      {commentPos && (
      <Card.Body>
        <Card.Subtitle className="mb-2">Does well:</Card.Subtitle>
        <MarkdownRenderer content={commentPos} />
      </Card.Body>
      )}
      {commentNeg && (
      <Card.Body>
        <Card.Subtitle className="mb-2">To Work On:</Card.Subtitle>
        <MarkdownRenderer content={commentNeg} />
      </Card.Body>
      )}
      {!comment && (!commentPos || !commentNeg) && (
      <Card.Body>
        <Icon icon="warning" />
        {' '}
        Section Incomplete
      </Card.Body>
      )}
      <Card.Footer>
        <UserIcon size={22} style={{ verticalAlign: 'top' }} user={reviewer} round />
        {' '}
        {reviewer.name}
        {' ('}
        {getRelation(type)}
        )
      </Card.Footer>
    </Card>
  );
}

function NewReviewInputModal({
  user, date, open, setOpen, refreshView,
}) {
  const [categories, setCategories] = useState([]);
  const [categoryData, setCategoryData] = useState([]);

  const initCategories = async () => {
    const params = {};

    if (date) {
      params.review_period = moment(date).format('YYYY-MM-DD');
    }
    // Get latest categories for this month from the API
    const categoryDataForDate = await ReviewCategoriesResource.index(params);

    setCategories(categoryDataForDate.map((categoryAndSplit) => categoryAndSplit.category));
    // Set empty
    setCategoryData(categoryDataForDate.map((categoryAndSplit) => {
      const baseData = {
        category: categoryAndSplit.category,
        score: categoryAndSplit.category === 'other' ? null : 3,
      };
      if (categoryAndSplit.category === 'other' || !categoryAndSplit.is_split) {
        baseData.comment = '';
      } else {
        // assume split
        baseData.comment_pos = '';
        baseData.comment_neg = '';
      }
      return baseData;
    }));
  };

  useEffect(() => { initCategories(); }, [user, date]);

  return (
    <ReviewInputModal
      open={open}
      user={user}
      date={date}
      setOpen={setOpen}
      refreshView={refreshView}
      categories={categories}
      categoryData={categoryData}
      isNew
      splitComments
    />
  );
}

function EditReviewInputModal({
  review, user, date, open, setOpen, refreshView,
}) {
  const [categories, setCategories] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [splitComments, setSplitComments] = useState(null);

  const initCategories = () => {
    setCategories(review.data_by_category.map((reviewCategoryData) => reviewCategoryData.category));
    setCategoryData(review.data_by_category);
    setSplitComments(review.has_split_comments);
  };

  useEffect(() => { initCategories(); }, [review]);

  return (
    <ReviewInputModal
      open={open}
      user={user}
      date={date}
      setOpen={setOpen}
      refreshView={refreshView}
      categories={categories}
      categoryData={categoryData}
      splitComments={splitComments}
      reviewId={review.id}
      reviewPeriod={review.review_period}
      leadHasPublished={review.lead_has_published}
    />
  );
}

function CategoryConsideration({ category }) {
  switch (category) {
    case 'impact':
      return (
        <ul className="mb-0">
          <li>Are you meeting expectations in your role?</li>
          <li>Are you achieving all of the impact competencies for your current level?</li>
          <li>How well are you progressing with your impact objectives?</li>
          <li>Do you help other people to do their job better?</li>
          <li>What size and scale of work are you capable of leading?</li>
          <li>Do you support people from outside your immediate team?</li>
        </ul>
      );
    case 'knowledge':
      return (
        <ul className="mb-0">
          <li>Do you understand the systems and processes associated with your role?</li>
          <li>
            What level of specialist knowledge do you have that pertains to
            {' '}
            your role. Squad or the systems you work with?
          </li>
          <li>Do you actively look for opportunities to develop your knowledge and skills?</li>
          <li>How well are you progressing in your knowledge objectives?</li>
        </ul>
      );
    case 'behaviours':
      return (
        <ul className="mb-0">
          <li>Do you have the interpersonal skills required for your role?</li>
          <li>Are you an effective communicator within your team?</li>
          <li>Do your behaviours positively impact the people you work with?</li>
          <li>How well do you give and receive honest and open feedback?</li>
          <li>How well are you progressing in your behaviours objectives?</li>
        </ul>
      );
    case 'communication':
      return (
        <ul className="mb-0">
          <li>A clear communicator via written or verbal methods.</li>
          <li>Professional and projects a positive image at all times.</li>
          <li>Modifies personal style to suit the recipient.</li>
          <li>Shows active listening skills.</li>
          <li>Good at understanding other points of view.</li>
          <li>Easily builds rapport.</li>
          <li>Communicates messages clearly to avoid confusion.</li>
          <li>
            Diligent in following up on Slack messages,
            {' '}
            emails and actions to ensure issues are resolved.
          </li>
          <li>Effectively communicates with squad members, team lead and the wider team.</li>
        </ul>
      );
    case 'motivation':
      return (
        <ul className="mb-0">
          <li>Takes responsibility for their own area of work.</li>
          <li>Sets challenging targets and aims to exceed them.</li>
          <li>Demonstrates enthusiasm and interest in pursuit of goals.</li>
          <li>Overcomes obstacles and persists until tasks are completed.</li>
          <li>Has a positive ‘can-do’ attitude.</li>
          <li>Brings energy and pace to the department.</li>
          <li>Remains calm and composed in the face of setbacks or pressure.</li>
          <li>Maintains an openness to learning.</li>
          <li>Actively looks for ways to improve their performance.</li>
        </ul>
      );
    case 'team_working':
      return (
        <ul className="mb-0">
          <li>Takes an active role in the achievement of team objectives</li>
          <li>Puts team objectives ahead of self-interest</li>
          <li>Encourages and supports other team members</li>
          <li>Responds positively to requests for help or support</li>
          <li>Listen to others and adopts a win-win attitude</li>
          <li>Focuses on the importance of working together</li>
          <li>Uses strengths of others to get the job done</li>
          <li>Willingly puts in extra effort when required</li>
          <li>Ability to work well within the team,with management and across departments</li>
        </ul>
      );
    case 'role':
      return (
        <ul className="mb-0">
          <li>How well is the person performing in their current role?</li>
          <li>Do they meet the criteria outlined in their job description?</li>
          <li>How well are they progressing in their Role and Responsibilities objectives?</li>
        </ul>
      );
    default:
      return <ul />;
  }
}

function ReviewInputModal({
  user,
  date,
  open,
  setOpen,
  refreshView,
  categories,
  categoryData,
  isNew = false,
  splitComments = false,
  reviewId = null,
  reviewPeriod = null,
  leadHasPublished = null,
}) {
  const [pendingReview, setPendingReview] = useState(categoryData);
  const [id, setId] = useState(reviewId);
  const [published, setPublished] = useState(leadHasPublished);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setPendingReview(categoryData); }, [categoryData]);

  const handleClose = () => {
    setOpen(false);
    refreshView();
  };

  const getScore = (category) => pendingReview.find(
    (reviewData) => reviewData.category === category,
  )?.score;
  const getComment = (category, type = 'comment') => {
    const obj = pendingReview.find(
      (reviewData) => reviewData.category === category,
    );
    if (!obj) return null;
    const { [type]: toReturn } = obj; // deconstruct using variable and map
    return toReturn;
  };

  const scoreOptions = [
    { value: 1, label: ' Struggling in this area' },
    { value: 2, label: ' Getting better' },
    { value: 3, label: ' Doing well' },
    { value: 4, label: ' Exceeding expectations' },
    { value: 5, label: ' Outstanding' },
  ];

  const saveReview = async (referenceComment = null) => {
    // Don't retry if we have a previous error
    if (error !== '') {
      return;
    }

    // Validate we're saving to the correct period
    if (!date) {
      setError('No review period selected');
      return;
    }

    const reviewPeriodFormatted = moment(date).format('YYYY-MM-DD');

    // For existing reviews, validate the period matches
    if (id && reviewPeriod && reviewPeriodFormatted !== moment(reviewPeriod).format('YYYY-MM-DD')) {
      setError('Cannot save review to a different period');
      return;
    }

    const params = {
      review_period: reviewPeriodFormatted,
      data_by_category: pendingReview,
    };

    setSaving(true);

    try {
      if (id) {
      // edit
        const review = await UserReviewResource(user.id).put(id, params);
        setId(review.id);
        setPendingReview(review.data_by_category);
        setPublished(review.lead_has_published);
      } else {
      // new
        const review = await UserReviewResource(user.id).post(params);
        setId(review.id);
        setPendingReview(review.data_by_category);
        setPublished(review.lead_has_published);
      }
    } catch (saveError) {
      setError(
        `Something went wrong (details in console) - please copy what you just wrote and refresh the page: "${referenceComment}"`,
      );
      console.error(saveError);
    } finally {
      setSaving(false);
    }
  };

  const addComment = (comment, category, type = 'comment') => {
    const pending = [...pendingReview];
    (pending.find((reviewData) => reviewData.category === category))[type] = comment;
    setPendingReview(pending);
    saveReview(comment);
  };

  const addScore = (score, category) => {
    const pending = [...pendingReview];
    pending.find((reviewData) => reviewData.category === category).score = score;
    setPendingReview(pending);
    saveReview();
  };

  const saveAndClose = () => {
    saveReview().then(() => handleClose());
  };

  const getGuidance = (score) => {
    const guidance = {
      1: 'This is an area that needs work, may be underperforming here or finding it difficult to meet the expected level.',
      2: 'Working on this area, but still more to do. May have just started a new role and working up to the expected level. This is a good thing to work on over the next year.',
      3: 'Meeting our high standards in this area. This is where we expect most people to be. Completely, utterly able to accomplish what is needed.',
      4: 'Doing more than expected by the team, even at our high level. Does and teaches. Exceptional and noticeable skills. Goes over and above what is expected of them.',
      5: "Consistently exceptional. Regularly goes above and beyond. Even in our amazing team, we'd only expect one person to achieve this rating.",
    };
    return guidance[score];
  };

  return (
    <Modal size="lg" show={open} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {isNew ? 'Add Review' : 'Edit Review'}
          {' '}
          for
          {' '}
          {user.name}
          {' '}
          (
          {moment(date).format('MMMM-YYYY')}
          )
        </Modal.Title>
        {saving && <Spinner className="ml-2" style={{ height: 25, width: 25, margin: 'auto' }} animation="border" />}
      </Modal.Header>
      {error !== '' && <Alert variant="danger">{error}</Alert>}
      <Modal.Body style={{ maxHeight: '80vh', overflow: 'auto' }} className="px-0">
        {categories.map((category) => (
          <div key={category}>
            <Card.Header style={{
              position: 'sticky', top: '-1.1rem', background: 'white', zIndex: 2,
            }}
            >
              <Card.Title className="mb-0">{getTitle(category)}</Card.Title>
              {category !== 'other' && getSubtitle(category) !== '' && <Card.Subtitle className="small text-muted mt-1">{getSubtitle(category)}</Card.Subtitle>}
            </Card.Header>

            <Card md={12} className="mb-4">
              <Card.Body>
                {category !== 'other' && (
                <div>
                  <Alert variant="info">
                    <Alert.Heading className="pb-1">
                      Things to consider when assessing
                      {' '}
                      {getSimpleTitle(category)}
                      :
                    </Alert.Heading>
                    <CategoryConsideration category={category} />
                  </Alert>
                  <Form.Group>
                    <Form.Label>
                      {user.name}
                      {' '}
                      is
                      {' '}
                    </Form.Label>
                    <Select
                      defaultValue={scoreOptions.filter(
                        (option) => option.value === getScore(category),
                      )}
                      className="ml-2"
                      styles={{
                        container: (base) => ({ ...base, display: 'inline-block', minWidth: '30%' }),
                        menuPortal: (base) => ({ ...base, zIndex: 1071, marginTop: 0 }),
                        menu: (base) => ({
                          ...base, marginTop: 0, marginBottom: 0, border: '1px solid grey',
                        }),
                      }}
                      menuPortalTarget={document.body}
                      options={scoreOptions}
                      onChange={(selected) => addScore(selected.value, category)}
                    />
                    <Form.Text className="mt-2">
                      <i
                        className="fad fa-2x fa-info-circle mr-2"
                        style={{ verticalAlign: 'middle' }}
                      />
                      <span>{getGuidance(getScore(category))}</span>
                    </Form.Text>
                  </Form.Group>
                </div>
                )}
                {(!splitComments || category === 'other') && (
                <Form.Group>
                  <Form.Control as="textarea" placeholder="Comments" rows={5} onBlur={(event) => addComment(event.target.value, category)} defaultValue={getComment(category)} />
                </Form.Group>
                )}
                {splitComments && category !== 'other' && (
                <div>
                  <Form.Group>
                    <Form.Label>What is going well?</Form.Label>
                    <Form.Text className="text-muted">
                      A few bullet points to highlight strengths.
                    </Form.Text>
                    <Form.Control as="textarea" rows={4} onBlur={(event) => addComment(event.target.value, category, 'comment_pos')} defaultValue={getComment(category, 'comment_pos')} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>What could be better?</Form.Label>
                    <Form.Text className="text-muted">
                      A few bullet points to highlight areas for focus.
                    </Form.Text>
                    <Form.Control as="textarea" rows={4} onBlur={(event) => addComment(event.target.value, category, 'comment_neg')} defaultValue={getComment(category, 'comment_neg')} />
                  </Form.Group>
                </div>
                )}
              </Card.Body>
            </Card>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <>
          <div style={{ width: '35%', marginRight: 'auto' }}>
            <span>Completed: </span>
            <CompletedPill reviewCategoryData={pendingReview} style={{ border: '1px #222 solid' }} />
          </div>
          <Button onClick={saveAndClose}>Save</Button>
        </>
        <div className="small info" style={{ marginRight: 'auto' }}>
          <p>
            Your review will not be visible to
            {' '}
            {user.name}
            {' '}
            until you complete all fields, and then
            {' '}
            {user.manager ? user.manager.name : `${user.name}'s manager`}
            {' '}
            will need to publish it.
          </p>
          <p>
            You will be able to edit your review until all fields are complete, and until
            {' '}
            {user.manager ? user.manager.name : `${user.name}'s manager`}
            {' '}
            has published their review.
          </p>
          <p>
            Once
            {' '}
            {user.manager ? user.manager.name : `${user.name}'s manager`}
            {' '}
            has published their review, your review will be visible to you,
            {' '}
            {user.name}
            , anybody that manages
            {' '}
            {user.name}
            {' '}
            through a squad or guild.
          </p>
          {published === true && (
          <p>
            Please note that
            {user.manager ? user.manager.name : `${user.name}'s manager`}
            {' '}
            has published their review
          </p>
          )}
          {published === false && (
          <p>
            {user.manager ? user.manager.name : `${user.name}'s manager`}
            {' '}
            has not published their review yet.
          </p>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default Reviews;
